import { mockResponse } from '@/utils/format'

const ENERGY_TYPES = [
  { key: 'electricity', label: '电' },
  { key: 'water', label: '水' },
  { key: 'coal', label: '煤' },
  { key: 'diesel', label: '柴油' },
  { key: 'gasoline', label: '汽油' },
  { key: 'gas', label: '天然气' },
  { key: 'lpg', label: '液化石油气' },
  { key: 'heat', label: '热力' }
]

const ENERGY_FACTOR = {
  electricity: 1,
  water: 0.35,
  coal: 0.72,
  diesel: 0.65,
  gasoline: 0.58,
  gas: 0.8,
  lpg: 0.75,
  heat: 0.9
}

const PERIOD_FACTOR = {
  day: 1,
  month: 30,
  year: 365
}

const SPACE_TREE = [
  {
    id: '1',
    label: '南京某某公司',
    children: [
      {
        id: '1-1',
        label: '1号厂区',
        children: [
          {
            id: '1-1-1',
            label: '机械加工生产线',
            children: [
              {
                id: '1-1-1-1',
                label: '下料',
                children: [
                  { id: '1-1-1-1-1', label: '起重机' },
                  { id: '1-1-1-1-2', label: '机械臂' }
                ]
              },
              { id: '1-1-1-2', label: '粗加工' }
            ]
          }
        ]
      },
      { id: '1-2', label: '2号厂区' },
      {
        id: '1-3',
        label: '办公楼',
        children: [{ id: '1-3-1', label: '1层' }, { id: '1-3-2', label: '2层' }]
      },
      { id: '1-4', label: '食堂' },
      { id: '1-5', label: '锅炉房' }
    ]
  }
]

const FLOW_LINKS = [
  { sourceId: '1', targetId: '1-1', value: 500 },
  { sourceId: '1', targetId: '1-2', value: 300 },
  { sourceId: '1', targetId: '1-3', value: 150 },
  { sourceId: '1-1', targetId: '1-1-1', value: 400 },
  { sourceId: '1-1-1', targetId: '1-1-1-1', value: 200 },
  { sourceId: '1-1-1', targetId: '1-1-1-2', value: 200 },
  { sourceId: '1-1-1-1', targetId: '1-1-1-1-1', value: 100 },
  { sourceId: '1-1-1-1', targetId: '1-1-1-1-2', value: 100 },
  { sourceId: '1-3', targetId: '1-3-1', value: 90 },
  { sourceId: '1-3', targetId: '1-3-2', value: 60 },
  { sourceId: '1-4', targetId: '1-4-loss', value: 80, isLoss: true }
]

const NODE_META = {
  '1': { label: '南京某某公司', level: 1 },
  '1-1': { label: '1号厂区', level: 2 },
  '1-2': { label: '2号厂区', level: 2 },
  '1-3': { label: '办公楼', level: 2 },
  '1-4': { label: '食堂', level: 2 },
  '1-5': { label: '锅炉房', level: 2 },
  '1-1-1': { label: '机械加工生产线', level: 3 },
  '1-1-1-1': { label: '下料', level: 4 },
  '1-1-1-2': { label: '粗加工', level: 5 },
  '1-1-1-1-1': { label: '起重机', level: 6 },
  '1-1-1-1-2': { label: '机械臂', level: 6 },
  '1-3-1': { label: '1层', level: 3 },
  '1-3-2': { label: '2层', level: 3 },
  '1-4-loss': { label: '食堂损耗', level: 3, isLoss: true }
}

const BALANCE_BY_NODE = {
  '1': { parentInput: 950, childSum: 750, imbalanceRate: 5 },
  '1-1': { parentInput: 500, childSum: 480, imbalanceRate: 3 },
  '1-1-1': { parentInput: 400, childSum: 357, imbalanceRate: 15 },
  '1-1-1-1': { parentInput: 200, childSum: 179, imbalanceRate: 8 },
  '1-1-1-1-1': { parentInput: 100, childSum: 95, imbalanceRate: 2 },
  '1-1-1-1-2': { parentInput: 100, childSum: 95, imbalanceRate: 2 },
  '1-1-1-2': { parentInput: 200, childSum: 190, imbalanceRate: 5 },
  '1-2': { parentInput: 300, childSum: 255, imbalanceRate: 12 },
  '1-3': { parentInput: 150, childSum: 142, imbalanceRate: 4 },
  '1-3-1': { parentInput: 90, childSum: 86, imbalanceRate: 4.4 },
  '1-3-2': { parentInput: 60, childSum: 56, imbalanceRate: 6.7 },
  '1-4': { parentInput: 80, childSum: 68, imbalanceRate: 11 }
}

let balanceRules = {
  threshold: 10,
  includeLoss: true,
  maxDepth: 5
}

function treeToCascader(nodes) {
  return nodes.map(node => ({
    value: node.id,
    label: node.label,
    children: node.children ? treeToCascader(node.children) : undefined
  }))
}

function findNodePath(nodes, targetId, path = []) {
  for (const node of nodes) {
    const currentPath = [...path, node.label]
    if (node.id === targetId) return currentPath
    if (node.children) {
      const found = findNodePath(node.children, targetId, currentPath)
      if (found) return found
    }
  }
  return null
}

function findNodeById(nodes, targetId) {
  for (const node of nodes) {
    if (node.id === targetId) return node
    if (node.children) {
      const found = findNodeById(node.children, targetId)
      if (found) return found
    }
  }
  return null
}

function collectDescendantIds(node) {
  const ids = [node.id]
  if (node.children) {
    node.children.forEach(child => {
      ids.push(...collectDescendantIds(child))
    })
  }
  return ids
}

function normalizeSpaceIds(spaceIds) {
  if (!spaceIds) return []
  if (Array.isArray(spaceIds)) return spaceIds.filter(Boolean)
  return String(spaceIds).split(',').filter(Boolean)
}

function getEffectiveRootIds(spaceIds) {
  if (!spaceIds.length) return ['1']
  const sorted = [...spaceIds].sort((a, b) => a.length - b.length)
  return sorted.filter(id => !sorted.some(other => other !== id && id.startsWith(`${other}-`)))
}

function getScopeNodeIds(rootIds) {
  const ids = new Set()
  rootIds.forEach(rootId => {
    const node = findNodeById(SPACE_TREE, rootId)
    if (node) {
      collectDescendantIds(node).forEach(id => ids.add(id))
    }
  })
  return ids
}

function scaleValue(value, energyType, period) {
  const energyFactor = ENERGY_FACTOR[energyType] || 1
  const periodFactor = PERIOD_FACTOR[period] || 1
  return Math.round(value * energyFactor * periodFactor)
}

function buildSankeyFromScope(scopeIds, rootIds, energyType, period, includeLoss) {
  const linkMap = new Map()

  FLOW_LINKS.forEach(link => {
    if (!includeLoss && link.isLoss) return
    if (scopeIds.has(link.sourceId) && scopeIds.has(link.targetId)) {
      linkMap.set(`${link.sourceId}-${link.targetId}`, link)
    }
  })

  rootIds.forEach(rootId => {
    const entryLink = FLOW_LINKS.find(link => link.targetId === rootId)
    if (entryLink && scopeIds.has(entryLink.targetId)) {
      linkMap.set(`${entryLink.sourceId}-${entryLink.targetId}`, entryLink)
    }
  })

  const links = Array.from(linkMap.values()).map(link => ({
    source: NODE_META[link.sourceId].label,
    target: NODE_META[link.targetId].label,
    value: scaleValue(link.value, energyType, period),
    sourceId: link.sourceId,
    targetId: link.targetId
  }))

  const nodeNames = new Set()
  links.forEach(link => {
    nodeNames.add(link.source)
    nodeNames.add(link.target)
  })

  rootIds.forEach(rootId => {
    if (NODE_META[rootId]) nodeNames.add(NODE_META[rootId].label)
  })

  const nodes = Array.from(nodeNames).map(name => ({ name }))
  return {
    nodes,
    links: links.map(({ source, target, value }) => ({ source, target, value }))
  }
}

function getNodeInput(nodeId, energyType, period, includeLoss) {
  const childLinks = FLOW_LINKS.filter(link => {
    if (link.sourceId !== nodeId) return false
    if (!includeLoss && link.isLoss) return false
    return true
  })
  if (childLinks.length) {
    return childLinks.reduce((sum, link) => sum + scaleValue(link.value, energyType, period), 0)
  }
  const parentLink = FLOW_LINKS.find(link => link.targetId === nodeId)
  return parentLink ? scaleValue(parentLink.value, energyType, period) : 0
}

function buildBalanceTable(scopeIds, energyType, period, rootIds) {
  const periodFactor = PERIOD_FACTOR[period] || 1
  const energyFactor = ENERGY_FACTOR[energyType] || 1
  const scale = value => Math.round(value * energyFactor * periodFactor)

  const rows = []
  rootIds.forEach(rootId => {
    const rootNode = findNodeById(SPACE_TREE, rootId)
    if (!rootNode) return

    const walk = node => {
      if (!scopeIds.has(node.id)) return
      const balance = BALANCE_BY_NODE[node.id]
      if (balance) {
        rows.push({
          level: NODE_META[node.id].level,
          difference: scale(Math.abs(balance.parentInput - balance.childSum)),
          imbalanceRate: balance.imbalanceRate
        })
      }
      if (node.children) node.children.forEach(child => walk(child))
    }

    walk(rootNode)
  })

  const levels = [...new Set(rows.map(row => row.level))].sort((a, b) => a - b)
  const minLevel = levels[0] || 1
  const LEVEL_COLUMNS = [
    '一级vs二级',
    '二级vs三级',
    '三级vs四级',
    '四级vs五级',
    '五级vs终端'
  ]

  return {
    columns: levels.map(level => LEVEL_COLUMNS[level - minLevel] || `L${level}vsL${level + 1}`),
    differences: levels.map(level => {
      const levelRows = rows.filter(row => row.level === level)
      return levelRows.reduce((sum, row) => sum + row.difference, 0)
    }),
    imbalanceRates: levels.map(level => {
      const levelRows = rows.filter(row => row.level === level)
      if (!levelRows.length) return 0
      return Number(
        (levelRows.reduce((sum, row) => sum + row.imbalanceRate, 0) / levelRows.length).toFixed(1)
      )
    })
  }
}

function buildSummary(scopeIds, sankey, balanceTable, threshold, energyType, period, rootIds) {
  const periodFactor = PERIOD_FACTOR[period] || 1
  const energyFactor = ENERGY_FACTOR[energyType] || 1
  const scale = value => Math.round(value * energyFactor * periodFactor)

  let totalInput = 0
  let totalConsumption = 0

  rootIds.forEach(rootId => {
    const balance = BALANCE_BY_NODE[rootId]
    if (balance) {
      totalInput += scale(balance.parentInput)
    } else {
      totalInput += getNodeInput(rootId, energyType, period, balanceRules.includeLoss)
    }
  })

  if (sankey.links.length) {
    const leafTargets = new Set(
      sankey.links
        .filter(link => !sankey.links.some(other => other.source === link.target))
        .map(link => link.target)
    )
    totalConsumption = sankey.links
      .filter(link => leafTargets.has(link.target))
      .reduce((sum, link) => sum + link.value, 0)
  } else {
    rootIds.forEach(rootId => {
      const balance = BALANCE_BY_NODE[rootId]
      if (balance) totalConsumption += scale(balance.childSum)
    })
  }

  const totalLoss = Math.max(totalInput - totalConsumption, 0)
  const lossRate = totalInput ? Number(((totalLoss / totalInput) * 100).toFixed(1)) : 0
  const abnormalLevels = balanceTable.imbalanceRates.filter(rate => rate > threshold).length

  return {
    totalInput,
    totalConsumption,
    totalLoss,
    lossRate,
    abnormalLevels,
    threshold,
    selectedRoots: rootIds.map(id => NODE_META[id].label)
  }
}

function getStatus(rate, threshold) {
  if (rate >= threshold) return 'danger'
  if (rate >= threshold * 0.6) return 'warning'
  return 'normal'
}

const STATUS_LABEL = {
  normal: '正常',
  warning: '预警',
  danger: '异常'
}

const BALANCE_LIST = [
  {
    id: 'b1',
    spaceId: '1',
    spacePath: '南京某某公司',
    levelRelation: '一级 vs 二级',
    parentInput: 950,
    childSum: 750,
    difference: 200,
    imbalanceRate: 5
  },
  {
    id: 'b2',
    spaceId: '1-1',
    spacePath: '南京某某公司 > 1号厂区',
    levelRelation: '二级 vs 三级',
    parentInput: 500,
    childSum: 480,
    difference: 20,
    imbalanceRate: 3
  },
  {
    id: 'b3',
    spaceId: '1-1-1',
    spacePath: '南京某某公司 > 1号厂区 > 机械加工生产线',
    levelRelation: '三级 vs 四级',
    parentInput: 400,
    childSum: 357,
    difference: 43,
    imbalanceRate: 15
  },
  {
    id: 'b4',
    spaceId: '1-1-1-1',
    spacePath: '南京某某公司 > 1号厂区 > 机械加工生产线 > 下料',
    levelRelation: '四级 vs 五级',
    parentInput: 200,
    childSum: 179,
    difference: 21,
    imbalanceRate: 8
  },
  {
    id: 'b5',
    spaceId: '1-1-1-1-1',
    spacePath: '南京某某公司 > 1号厂区 > 机械加工生产线 > 下料 > 起重机',
    levelRelation: '五级 vs 终端',
    parentInput: 100,
    childSum: 95,
    difference: 5,
    imbalanceRate: 2
  },
  {
    id: 'b6',
    spaceId: '1-2',
    spacePath: '南京某某公司 > 2号厂区',
    levelRelation: '二级 vs 三级',
    parentInput: 300,
    childSum: 255,
    difference: 45,
    imbalanceRate: 12
  },
  {
    id: 'b7',
    spaceId: '1-3',
    spacePath: '南京某某公司 > 办公楼',
    levelRelation: '二级 vs 三级',
    parentInput: 150,
    childSum: 142,
    difference: 8,
    imbalanceRate: 4
  },
  {
    id: 'b8',
    spaceId: '1-4',
    spacePath: '南京某某公司 > 食堂',
    levelRelation: '二级 vs 终端',
    parentInput: 80,
    childSum: 68,
    difference: 12,
    imbalanceRate: 11
  }
]

function buildTrend(threshold) {
  return [
    { date: '2025-07-01', rate: 5.2 },
    { date: '2025-07-02', rate: 6.8 },
    { date: '2025-07-03', rate: 4.1 },
    { date: '2025-07-04', rate: 8.5 },
    { date: '2025-07-05', rate: 7.3 },
    { date: '2025-07-06', rate: 9.1 },
    { date: '2025-07-07', rate: threshold > 8 ? 6.2 : 5.8 }
  ]
}

export function getAnalysisData(params = {}) {
  const energyType = params.energyType || 'electricity'
  const period = params.period || 'day'
  const threshold = params.threshold || balanceRules.threshold
  const includeLoss = params.includeLoss != null ? params.includeLoss : balanceRules.includeLoss
  const spaceIds = normalizeSpaceIds(params.spaceIds)
  const rootIds = getEffectiveRootIds(spaceIds)
  const scopeIds = getScopeNodeIds(rootIds)

  const energyTypes = ENERGY_TYPES.map(item => ({
    ...item,
    active: item.key === energyType
  }))

  const sankey = buildSankeyFromScope(scopeIds, rootIds, energyType, period, includeLoss)
  const balanceTable = buildBalanceTable(scopeIds, energyType, period, rootIds)
  const summary = buildSummary(scopeIds, sankey, balanceTable, threshold, energyType, period, rootIds)

  return mockResponse({
    energyTypes,
    spaceTree: SPACE_TREE,
    spaceCascader: treeToCascader(SPACE_TREE),
    selectedSpaceIds: spaceIds,
    summary,
    balanceTable,
    sankey
  })
}

export function getBalanceData(params = {}) {
  const threshold = params.threshold != null ? Number(params.threshold) : balanceRules.threshold
  const spaceId = params.spaceId

  let list = BALANCE_LIST.map(item => ({
    ...item,
    status: getStatus(item.imbalanceRate, threshold),
    statusLabel: STATUS_LABEL[getStatus(item.imbalanceRate, threshold)]
  }))

  if (spaceId) {
    list = list.filter(item => item.spaceId === spaceId || item.spaceId.startsWith(`${spaceId}-`))
  }

  const abnormalNodes = list.filter(item => item.status !== 'normal').length
  const avgImbalanceRate = list.length
    ? Number((list.reduce((sum, item) => sum + item.imbalanceRate, 0) / list.length).toFixed(1))
    : 0

  return mockResponse({
    energyTypes: ENERGY_TYPES,
    spaceCascader: treeToCascader(SPACE_TREE),
    summary: {
      checkedNodes: list.length,
      abnormalNodes,
      avgImbalanceRate
    },
    trend: buildTrend(threshold),
    list,
    rules: { ...balanceRules, threshold }
  })
}

export function getBalanceRules() {
  return mockResponse({ ...balanceRules })
}

export function saveBalanceRules(data) {
  balanceRules = { ...balanceRules, ...data }
  return mockResponse({ ...balanceRules })
}

export function getSpacePath(spaceId) {
  const path = findNodePath(SPACE_TREE, spaceId)
  return path ? path.join(' > ') : ''
}
